import { DepositPortfolioResponse } from './portfolio-response.model';
import { PortfolioAsset } from './portfolio.model';
import { getBreakEvenPrice, getProfitLossPercent, getTotalCost } from './portfolio.utils';

/**
 * Maps a `/portfolios/deposit` holding onto the model the asset cards consume.
 *
 * Absent numerics become 0 rather than being guessed: the card always shows all
 * eight figures, so a missing value has to render as a number. The one exception
 * is the cost basis — an absent average buy price leaves `totalCost` null, so the
 * holding sits out of the portfolio percentage instead of entering it as a
 * break-even position (see `getTotalProfitLossPercent`).
 *
 * Profit/loss starts as the API's own `totalProfit`, and today's figure as its
 * `todayTotalProfit` — all a holding with no price yet can be shown. Once a price
 * and the symbol's commission rate arrive both are recomputed from them — see
 * `withPrice` and `withCommissionRate`.
 *
 * The price-derived figures are left at 0 here — `/portfolios/deposit` carries no
 * price at all — and are filled in by `withPrice` once market data arrives. The
 * sell average and the trade counts are carried for the detail screen, which
 * multiplies each count by its average to get the buy/sell values the endpoint
 * does not report directly. The commission *rate* is not in this response at
 * all — only the amount already paid — so it starts at 0 and comes from
 * the symbol snapshot. The response's remaining fields (commission, today's buys, last
 * trade date) have no consumer yet.
 */
export function mapDepositPortfolioToAsset(dto: DepositPortfolioResponse): PortfolioAsset {
	const profitLoss = dto.totalProfit ?? 0;
	const asset: PortfolioAsset = {
		contractId: dto.contractId,
		symbolISIN: dto.symbolIsin,
		symbolName: dto.symbolName ?? dto.symbolIsin,
		symbolShortName: dto.symbolShortName ?? null,
		quantity: dto.remainingQuantity ?? 0,
		averageBuyPrice: dto.averageBuyPrice ?? 0,
		sellCommissionRate: 0,
		price: 0,
		priceChange: 0,
		priceChangePercent: 0,
		lastSettlementPrice: 0,
		closingPrice: 0,
		// The stored مبنای قیمت is applied by `PortfolioStore` once the holding is
		// in state; آخرین قیمت stands in here so an unpriced holding is on a valid
		// basis from the moment it is mapped.
		priceBasis: 'lastTradedPrice',
		netSaleValue: 0,
		profitLoss,
		profitLossPercent: 0,
		todayProfitLoss: dto.todayTotalProfit ?? 0,
		breakEvenPrice: getBreakEvenPrice(dto.averageBuyPrice ?? 0, dto.commission ?? 0),
		averageSellPrice: dto.averageSellPrice ?? 0,
		totalBuyCount: dto.totalBuyCount ?? 0,
		totalSellCount: dto.totalSellCount ?? 0,
		totalCost: null
	};

	// A holding with no recorded buy average has no measurable basis. Coercing it
	// to 0 would report the whole position as profit, so it stays null.
	const totalCost = getTotalCost(asset);

	return {
		...asset,
		totalCost,
		profitLossPercent: getProfitLossPercent(profitLoss, totalCost ?? 0)
	};
}
